import React, { useState, useEffect } from 'react';
import { X, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { commissionsService } from '../../services';

const money = (value) => Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const when = (value) => (value ? new Date(String(value).replace(' ', 'T')).toLocaleString() : '');

const PaymentHistoryModal = ({ onClose }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    commissionsService.getPaymentBatches()
      .then(setBatches)
      .catch(() => setError('Could not load payment history.'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (details[id]) return;
    setDetailLoading(true);
    try {
      const data = await commissionsService.getPaymentBatch(id);
      setDetails(current => ({ ...current, [id]: data.items }));
    } catch (e) {
      setError('Could not load that payment batch.');
    } finally {
      setDetailLoading(false);
    }
  };

  const download = async (batch) => {
    try {
      const response = await commissionsService.downloadPaymentBatchCsv(batch.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = batch.csv_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not download that file.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Payment History</h2>
            <p className="text-sm text-gray-500 mt-1">Every Pay &amp; Export is saved here. Open a payment to see what was in it, or download the same file again.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading payment history...</div>
          ) : batches.length === 0 ? (
            <div className="text-center text-gray-500 py-10">No payments have been exported yet. New payments will appear here.</div>
          ) : (
            <div className="space-y-2">
              {batches.map(batch => (
                <div key={batch.id} className="border rounded-lg">
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <button onClick={() => toggle(batch.id)} className="flex items-center gap-2 text-left flex-1 min-w-[200px]">
                      {openId === batch.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span>
                        <span className="block text-sm font-medium">{when(batch.created_at)}</span>
                        <span className="block text-xs text-gray-500">
                          {batch.commission_count} commission(s) &middot; {batch.agent_count} agent(s) &middot; by {batch.created_by_name || 'Admin'}
                        </span>
                      </span>
                    </button>
                    <span className="text-sm font-semibold">{money(batch.total_amount)}</span>
                    <button onClick={() => download(batch)} className="px-3 py-1.5 text-xs bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center">
                      <Download className="w-3.5 h-3.5 mr-1" />CSV
                    </button>
                  </div>
                  {openId === batch.id && (
                    <div className="border-t px-4 py-3 bg-gray-50 text-sm">
                      {detailLoading && !details[batch.id] ? 'Loading...' : (
                        <table className="w-full text-left">
                          <thead>
                            <tr className="text-xs text-gray-500">
                              <th className="py-1 pr-3 font-medium">Agent</th>
                              <th className="py-1 pr-3 font-medium">Reservation</th>
                              <th className="py-1 text-right font-medium">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(details[batch.id] || []).map(item => (
                              <tr key={item.commission_id} className="border-t border-gray-200">
                                <td className="py-1 pr-3">{item.agent_name || `Agent ${item.agent_id}`}</td>
                                <td className="py-1 pr-3">{item.reservation_number}</td>
                                <td className="py-1 text-right">{money(item.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;
